import { useTranslation } from 'react-i18next';

import { PROTOCOL } from '@/entities/channel';
import { useSubscriptionByChannelId } from '@/features/billing';
import {
  ChangeChannelNameForm,
  CHANNEL_NAME_MAP,
  ChannelIconByType,
  ChannelStatus,
  useChannelById,
} from '@/features/channels';
import { Stack } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { CustomTypography } from '@/shared/ui/Typography';

import { ChangeBotBlock } from './ChangeBotBlock';
import { ChangePhoneNumberBlock } from './ChangePhoneNumberBlock';
import { AvitoAuthorizationBlock } from './AvitoAuthorizationBlock';
import { DeleteMaxPersonalConnectionButton } from './DeleteMaxPersonalConnectionButton';
import { SettingBlockItem } from './SettingBlockItem';
import { SettingsDeleteChannelButton } from './SettingsDeleteChannelButton';
import { hasActiveSubscription } from './channelDeletion';
import {
  canChangeChannelPhone,
  getChannelSettingsPhone,
} from './channelSettingsPhone';
import { canDeleteMaxPersonalConnection } from './maxPersonalConnection';
import {
  StyledCardBody,
  StyledCardFooter,
  StyledCardHeader,
  StyledChannelCard,
} from './styles';

export const ChannelSettingsTab = ({ channelId }: { channelId: string }) => {
  const { t } = useTranslation();

  const { channelByIdData, isPending } = useChannelById(channelId, {
    refetchOnMount: 'always', // для обновления статуса канала при переходе на таб
  });
  const {
    data: subscriptionResponse,
    endDate,
    isLoading: isSubscriptionLoading,
  } = useSubscriptionByChannelId(channelId, {
    refetchOnMount: 'always', // Подписка на бэкенде обновляется с задержкой, поэтому нужно запрашивать актуальные данные при заходе на таб
    customError: true,
  });

  if (!channelByIdData || isPending) {
    return <Loader containerSize="medium" />;
  }

  // Bot channels are token-based. Phone-change is limited to protocols where
  // reset does not create a fresh partner-side instance on dialog open.
  const isTokenBot =
    channelByIdData.protocol === PROTOCOL.TELEGRAM_BOT ||
    channelByIdData.protocol === PROTOCOL.MAX;
  const canChangePhone = canChangeChannelPhone(channelByIdData);
  const hasActiveChannelSubscription = hasActiveSubscription(
    subscriptionResponse?.end_date
  );
  const deleteDisabledReason = hasActiveChannelSubscription
    ? t('channels:deleteChannelActiveSubscription')
    : isSubscriptionLoading
      ? t('channels:deleteChannelSubscriptionLoading')
      : undefined;
  let connectionSettingsBlock: React.ReactNode;

  if (isTokenBot) {
    connectionSettingsBlock = <ChangeBotBlock channel={channelByIdData} />;
  } else if (String(channelByIdData.protocol) === PROTOCOL.AVITO) {
    connectionSettingsBlock = <AvitoAuthorizationBlock channel={channelByIdData} />;
  } else if (canChangePhone) {
    connectionSettingsBlock = <ChangePhoneNumberBlock channel={channelByIdData} />;
  } else {
    connectionSettingsBlock = (
      <SettingBlockItem title={t('channels:settings.number')}>
        <CustomTypography color="text.secondary">
          {getChannelSettingsPhone(channelByIdData) || '-'}
        </CustomTypography>
        {canDeleteMaxPersonalConnection(channelByIdData) ? (
          <DeleteMaxPersonalConnectionButton channel={channelByIdData} />
        ) : null}
      </SettingBlockItem>
    );
  }

  return (
    <StyledChannelCard>
      <StyledCardHeader>
        <ChannelIconByType type={channelByIdData.protocol} avatarUrl={channelByIdData.avatar_url} />
        <CustomTypography>
          {t(CHANNEL_NAME_MAP[channelByIdData.protocol])}
        </CustomTypography>
      </StyledCardHeader>
      <StyledCardBody
        sx={
          isTokenBot
            ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 20 }
            : {}
        }
      >
        <SettingBlockItem title={t('billing:subscriptionExpiration')}>
          <CustomTypography color="text.secondary">{endDate}</CustomTypography>
        </SettingBlockItem>

        {connectionSettingsBlock}

        <SettingBlockItem title={t('channels:listTable.status')}>
          <Stack direction="row" spacing={2}>
            <ChannelStatus status={channelByIdData.status} protocol={channelByIdData.protocol} />
          </Stack>
        </SettingBlockItem>
        <SettingsDeleteChannelButton
          channelId={channelId}
          disabled={isSubscriptionLoading || hasActiveChannelSubscription}
          disabledReason={deleteDisabledReason}
        />
      </StyledCardBody>

      {!isTokenBot && (
        <StyledCardFooter>
          <ChangeChannelNameForm
            channelId={channelId}
            defaultName={channelByIdData.name}
          />
        </StyledCardFooter>
      )}
    </StyledChannelCard>
  );
};
